import React from 'react';
import styled from '@emotion/styled';

import { CrossIcon } from '../Icons/CrossIcon';
import { __ } from '../../helpers/TranslationService';
import { config } from '../../config';
import { Button } from '../Button/Button';
import { validateEmail } from '../../helpers/validation';

import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';

interface NewsletterSubscribeScreenProps {
    className?: string;
    onBannerCloseHandler: () => void;
    isGoogleSSOEnabled?: boolean;
    isAppleSSOEnabled?: boolean;
    isNetIDSSOEnabled?: boolean;
    isHotmailSSOEnabled?: boolean;
    handleGoogleSSO: () => void;
    handleHotmailSSO: () => void;
    handleAppleSSO: () => void;
    handleNetIDSSO: () => void;
    handleEmailSubscription: (email: string, method: string) => void;
    onStepBack: () => void;
    isLoading?: boolean;
}

const SSO_BUTTON_COLORS = {
    google: '#4283F3',
    apple: '#000',
    gmx: '#164597',
    hotmail: '#0078D4',
};

const Container = styled.div``;

const Header = styled.div`
    padding: 16px;

    display: flex;
    flex-direction: row;
    align-items: center;

    border-bottom: 1px solid ${(props) => props.theme.border.onBg};

    *:last-child {
        margin-left: auto;
    }

    svg {
        fill: ${(props) => props.theme.icon.secondary};
    }
`;

const Body = styled.div`
    background: ${(props) => props.theme.background.page};
    padding: 16px;
`;

const Footer = styled.div`
    background: ${(props) => props.theme.background.page};
    padding: 16px;
    padding-top: 0;
`;

const HeaderTitle = styled.p`
    color: #000;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px; /* 157.143% */
    margin: 0 8px;
`;

const NewsletterHelperText = styled.p`
    color: ${(props) => props.theme.text.color.primary2};
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    margin: 0;

    a {
        text-decoration: underline;
    }
`;

const SSOButton = styled('button')<{
    ssoType?: keyof typeof SSO_BUTTON_COLORS;
}>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 8px;
    border-radius: 8px;
    border: none;
    padding: 4px;
    width: 100%;
    color: white;

    background: ${(props) =>
        props.ssoType ? SSO_BUTTON_COLORS[props.ssoType] : props.theme.button.primary.background};

    div {
        background: white;
        border-radius: 6px;
        padding: 8px;
    }

    img {
        width: 20px;
    }

    div + p {
        display: flex;
        flex: 1;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;
        justify-content: center;
        margin: 0;
    }
`;

const TextDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    user-select: none;
    margin: 16px 0;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${(props) => props.theme.border.onBg};
        boder-radius: 1px;
    }

    > * {
        color: black;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px;
    }
`;

const EmailInputContainer = styled.div`
    display: flex;

    button {
        width: 48px;
        padding: 14px;
    }
`;

const StyledInput = styled.input<{ hasError: boolean }>`
    flex: 1;
    font-size: 14px;
    margin-right: 8px;
    border-radius: 8px;
    border: 2px solid;
    background: ${(props) => props.theme.input.backgroundColor};
    padding: 12px;
    border-color: ${({ hasError, theme }) =>
        hasError ? theme.colors.watermelon[100] : theme.input.borderColor};

    &:focus {
        outline-color: ${(props) => props.theme.input.borderColorActive};
    }
`;

const StyledErrorMessage = styled.p`
    color: ${(props) => props.theme.text.color.error};
    font-size: 14px;
    font-style: normal;
    margin: 0;
`;

export const NewsletterSubscribeScreen: React.FC<NewsletterSubscribeScreenProps> = ({
    className,
    onBannerCloseHandler,
    isGoogleSSOEnabled,
    isAppleSSOEnabled,
    isHotmailSSOEnabled,
    isNetIDSSOEnabled,
    handleGoogleSSO,
    handleHotmailSSO,
    handleAppleSSO,
    handleNetIDSSO,
    handleEmailSubscription,
    onStepBack,
    isLoading,
}) => {
    const [state, setState] = React.useState({
        email: '',
        hasError: false,
    });

    const handleChange = (email: string) => {
        setState({ ...state, email: email.trim(), hasError: false });
    };

    const handleSubmit = () => {
        setState({ ...state });

        const isValidEmail = validateEmail(state.email);

        if (isValidEmail) {
            setState({ ...state, hasError: false });
            handleEmailSubscription(state.email, 'email');
        } else {
            setState({ ...state, hasError: true });
        }
    };

    React.useEffect(() => {
        eventTracker.trackG4Event('newsletter', {
            step: 'sso',
        });
    }, []);

    return (
        <Container className={className}>
            <Header>
                <Button
                    paddings={false}
                    rounded={false}
                    color="flatSecondary"
                    size="small"
                    onClick={onStepBack}
                >
                    <ArrowLeftIcon width={16} height={16} />
                </Button>

                <HeaderTitle> {__('newsletter.bannerHeadTitle', 'dreamlines')} </HeaderTitle>

                <Button
                    paddings={false}
                    rounded={false}
                    color="flatSecondary"
                    size="small"
                    onClick={onBannerCloseHandler}
                >
                    <CrossIcon width={16} height={16} />
                </Button>
            </Header>

            <Body>
                {isGoogleSSOEnabled && (
                    <SSOButton
                        ssoType="google"
                        disabled={isLoading}
                        color="flatSecondary"
                        onClick={handleGoogleSSO}
                    >
                        <div>
                            <img src={`${config.images}/common/google-sso.svg`} alt="Google SSO" />
                        </div>
                        <p>
                            {__('newsletter.loginWithProvider', 'dreamlines').replace(
                                '{{method}}',
                                'Google',
                            )}
                        </p>
                    </SSOButton>
                )}
                {isHotmailSSOEnabled && (
                    <SSOButton
                        ssoType="hotmail"
                        disabled={isLoading}
                        color="flatSecondary"
                        onClick={handleHotmailSSO}
                    >
                        <div>
                            <img src={`${config.images}/common/outlook.svg`} alt="Hotmail SSO" />
                        </div>
                        <p>
                            {__('newsletter.loginWithProvider', 'dreamlines').replace(
                                '{{method}}',
                                'Hotmail',
                            )}
                        </p>
                    </SSOButton>
                )}
                {isAppleSSOEnabled && (
                    <SSOButton
                        ssoType="apple"
                        disabled={isLoading}
                        color="flatSecondary"
                        onClick={handleAppleSSO}
                    >
                        <div>
                            <img src={`${config.images}/common/apple-sso.svg`} alt="Apple SSO" />
                        </div>
                        <p>
                            {__('newsletter.loginWithProvider', 'dreamlines').replace(
                                '{{method}}',
                                'Apple',
                            )}
                        </p>
                    </SSOButton>
                )}
                {isNetIDSSOEnabled && (
                    <SSOButton
                        ssoType="gmx"
                        disabled={isLoading}
                        color="flatSecondary"
                        onClick={handleNetIDSSO}
                    >
                        <div>
                            <img src={`${config.images}/common/gmx-sso.svg`} alt="GMX SSO" />
                        </div>
                        <p>
                            {__('newsletter.loginWithProvider', 'dreamlines').replace(
                                '{{method}}',
                                'GMX',
                            )}
                        </p>
                    </SSOButton>
                )}

                <TextDivider aria-hidden="false" role="separator">
                    <span>{__('newsletter.enterEmailManually', 'dreamlines')}</span>
                </TextDivider>

                <EmailInputContainer>
                    <StyledInput
                        name="email"
                        hasError={state.hasError}
                        type="email"
                        disabled={isLoading}
                        value={state.email}
                        placeholder={__('your mail address', 'dreamlines')}
                        onChange={(e) => handleChange(e.target.value)}
                    />

                    <Button
                        size="medium"
                        loading={isLoading}
                        disabled={state.hasError}
                        onClick={handleSubmit}
                    >
                        <ArrowRightIcon width={20} height={20} style={{ fill: 'white' }} />
                    </Button>
                </EmailInputContainer>

                {state.hasError && (
                    <StyledErrorMessage>{__('incorrect email', 'dreamlines')}</StyledErrorMessage>
                )}
            </Body>

            <Footer>
                <NewsletterHelperText
                    dangerouslySetInnerHTML={{
                        __html: __('newsletter related GDPR text', 'dreamlines'),
                    }}
                />
            </Footer>
        </Container>
    );
};
